/**
 * Worksteps Store
 *
 * Manages workstep state for worksheets, including navigation through workstep history.
 */

import { type SetStoreState, createStore } from '@seeqdev/utils';
import { createStoreInstance } from '@seeqdev/utils';

export interface WorkstepsStoreState {
  previous?: string;
  current: { id?: string; state?: object; createdAt?: number };
  next?: string;
  last?: string;
  isError: boolean;
}

export interface WorkstepsStoreActions {
  setWorksteps: (payload: Partial<WorkstepsStoreState>) => void;
  reset: () => void;
}

const initialState: WorkstepsStoreState = {
  previous: undefined,
  current: {
    id: undefined,
    state: {},
    createdAt: undefined,
  },
  next: undefined,
  last: undefined,
  isError: false,
};

const workstepsActionsFactory = (set: SetStoreState<WorkstepsStoreState>): WorkstepsStoreActions => ({
  /**
   * Sets workstep state
   *
   * @param payload - Object container for workstep state
   * @param payload.previous - The ID of the previous workstep
   * @param payload.current - Object container for current state
   * @param payload.current.id - The ID of the current workstep
   * @param payload.current.state - The current workstep state
   * @param payload.next - The ID of the next workstep
   * @param payload.last - The ID of the last workstep
   * @param payload.isError - True if data is empty due to an error
   */
  setWorksteps: (payload: Partial<WorkstepsStoreState>) =>
    set((state) => {
      Object.assign(state, payload);
    }),

  /** Clears workstep state (resets to initial state) */
  reset: () =>
    set(() => ({
      previous: undefined,
      current: {
        id: undefined,
        state: {},
        createdAt: undefined,
      },
      next: undefined,
      last: undefined,
      isError: false,
    })),
});

const STORE_NAME = 'Worksteps';

const [WorkstepsStoreProvider, useWorkstepsStoreInstance, useWorkstepsSelector] = createStore(
  STORE_NAME,
  initialState,
  workstepsActionsFactory,
);

export { WorkstepsStoreProvider };

/**
 * Hook that only re-renders when previous changes
 */
export const usePreviousWorkstep = () => useWorkstepsSelector((state) => state.previous);

/**
 * Hook that only re-renders when current changes
 */
export const useCurrentWorkstep = () => useWorkstepsSelector((state) => state.current);

/**
 * Hook that only re-renders when current.id changes.
 * Prefer this over useCurrentWorkstep() when only the id is needed,
 * to avoid subscribing to the full workstep state object.
 */
export const useCurrentWorkstepId = () => useWorkstepsSelector((state) => state.current.id);

/**
 * Hook that only re-renders when current.createdAt changes.
 */
export const useCurrentWorkstepCreatedAt = () => useWorkstepsSelector((state) => state.current.createdAt);

/**
 * Hook that only re-renders when next changes
 */
export const useNextWorkstep = () => useWorkstepsSelector((state) => state.next);

/**
 * Hook that only re-renders when last changes
 */
export const useLastWorkstep = () => useWorkstepsSelector((state) => state.last);

/**
 * Hook that only re-renders when isError changes
 */
export const useIsWorkstepError = () => useWorkstepsSelector((state) => state.isError);

/**
 * Hook that provides action functions
 */
export const useWorkstepsActions = () => useWorkstepsStoreInstance().actions;

export type WorkstepsStore = ReturnType<typeof legacyCreateWorkstepsStore>;

/**
 * Legacy function to create a singleton Worksteps store instance.
 *
 * @deprecated This is only for backward compatibility with non-React code (e.g., action files, utilities).
 * New code should use the WorkstepsStoreProvider and hooks instead.
 */
export function legacyCreateWorkstepsStore(initialStateOverrides?: Partial<WorkstepsStoreState>) {
  return createStoreInstance(
    initialStateOverrides ? { ...initialState, ...initialStateOverrides } : initialState,
    workstepsActionsFactory,
    { storeName: STORE_NAME },
  );
}
